import { Box, Button, Heading, useToast } from '@chakra-ui/react'
import { FileGenerator } from 'components/molecules/FileGenerator'
import { useInstitutionsContext } from 'contexts/institutions'
import { useGenerateFiles } from 'hooks/useGenerateFIles'
import { GlobalFilter } from 'pages/components/GlobalFilter'
import { useRef, useState } from 'react'
import {
    getDashboardBillingReports,
    getFilesBillingReports
} from 'services/billing'
import { generateFile } from 'utils/blob'
import formatter from 'utils/formatter'
import { removeEmptyValues } from 'utils/removeEmptyValues'
import { form } from './form'
import {
    BillingQueryDTO,
    DocumentTypes,
    FormFilter,
    IModeTableRef,
    TModes
} from './interface'
import ModeDashboard from './ModeDashboard'
import { ModeTable } from './ModeTable'
import { schema } from './schema'

export const Billing = () => {
    const [filters, setFilters] = useState<FormFilter>({})
    const [loadingFilter, setLoadingFilter] = useState<boolean>(false)
    const [loadingFile, setLoadingFile] = useState<boolean>(false)
    const setCurrentFilters = (value: FormFilter) => {
        refModeTable?.current?.resetPage && refModeTable.current.resetPage()
        setFilters(value)
    }
    const [currentMode, setCurrentMode] = useState<TModes>('table')
    const refModeTable = useRef<IModeTableRef>(null)
    const toast = useToast()
    const modeTable = currentMode === 'table'
    const { getImageBase64Dashboards } = useGenerateFiles()
    const { currentInstitution, currentAgent } = useInstitutionsContext()
    const labelMode = !modeTable ? 'Modo Tabela' : 'Modo Dashboard'
    const onChangeMode = () => {
        const newMode = modeTable ? 'dashboard' : 'table'
        setCurrentMode(newMode)
    }

    const callAPI = (type: DocumentTypes) => {
        downloadFiles(type)
    }

    const buildPayload = (): Omit<BillingQueryDTO, 'pagina' | 'limite'> => {
        const payload = removeEmptyValues({
            cnpjAgente: currentAgent?.cnpj ?? '',
            instituicaoFinanceira: currentInstitution?.id ?? '',
            ...filters,
            periodoInicial: filters?.periodoInicial
                ? formatter.formatDateToPayload(filters?.periodoInicial)
                : '',
            periodoFinal: filters?.periodoFinal
                ? formatter.formatDateToPayload(filters?.periodoFinal)
                : ''
        })

        return payload
    }

    const downloadFiles = async (type: DocumentTypes) => {
        try {
            setLoadingFile(true)
            if (!modeTable) {
                const imageBase64 = await getImageBase64Dashboards(
                    'billing-dashboard'
                )

                if (imageBase64) {
                    const response = await getDashboardBillingReports(
                        buildPayload(),
                        imageBase64
                    )

                    generateFile('PDF', response)

                    toast({
                        title: 'Arquivo gerado com sucesso!',
                        description: `O arquivo ${type} foi gerado conforme os filtros selecionados. Verifique sua pasta de downloads para acessá-lo.`,
                        position: 'bottom-left',
                        status: 'success',
                        isClosable: true
                    })

                    return
                }
                return
            }

            const response = await getFilesBillingReports(buildPayload(), type)

            generateFile(type, response)

            toast({
                title: 'Arquivo gerado com sucesso!',
                description: `O arquivo ${type} foi gerado conforme os filtros selecionados. Verifique sua pasta de downloads para acessá-lo.`,
                position: 'bottom-left',
                status: 'success',
                isClosable: true
            })
        } catch (error) {
            toast({
                title: 'Sentimos muito!',
                description: 'Ocorreu algum erro, tente novamente mais tarde.',
                position: 'bottom-left',
                status: 'error',
                isClosable: true
            })
        } finally {
            setLoadingFile(false)
        }
    }

    return (
        <>
            <Box
                display="flex"
                justifyContent="space-between"
                margin="24px 0"
                gap="8px"
            >
                <Heading size="md">Gestão Faturamento</Heading>
                <Box gap={'4'} flex={'row'}>
                    <Button
                        marginRight={'8px'}
                        colorScheme="primary"
                        type="submit"
                        onClick={onChangeMode}
                    >
                        {labelMode}
                    </Button>
                    <FileGenerator
                        callAPI={callAPI}
                        onlyPDF={!modeTable}
                        loading={loadingFile}
                    />
                </Box>
            </Box>

            <GlobalFilter
                schema={schema}
                form={form}
                actionFilter={setCurrentFilters}
                loadingFilter={loadingFilter}
                title="Filtrar Relatório(s)"
            />

            {modeTable && (
                <ModeTable
                    ref={refModeTable}
                    filter={filters}
                    setLoadingFilter={setLoadingFilter}
                />
            )}
            {!modeTable && (
                <ModeDashboard
                    idDashboard="billing-dashboard"
                    filter={filters}
                    setLoadingFilter={setLoadingFilter}
                />
            )}
        </>
    )
}
